const REDACTED = '[REDACTED]';
const credentialKeys = new Set([
  'authorization', 'proxyauthorization', 'xriskauthorization', 'cookie', 'cookies', 'setcookie', 'auth',
  'token', 'accesstoken', 'idtoken', 'refreshtoken', 'authtoken', 'bearertoken',
  'password', 'passwd', 'clientsecret', 'secret', 'apikey', 'xapikey',
  'xauthtoken', 'xcsrftoken', 'csrftoken', 'sessionkey', 'dek', 'jwt',
  'tokens', 'deksessionid', 'xamzsecuritytoken', 'awsaccesskeyid', 'awssecretaccesskey',
  'tokenid', 'sessionid', 'castgc', 'iplanetdirectorypro',
  'recaptchatokenv2',
]);
const isCredentialKey = key => {
  const normalized = String(key).replace(/[^a-z0-9]/gi, '').toLowerCase();
  return credentialKeys.has(normalized) || /(?:token|secret|password|privatekey)$/.test(normalized);
};

const MAX_TEXT_LENGTH = 65536;
const MAX_DEPTH = 12;

const redactQuery = (match, separator, key) => {
  try {
    // Decode names only: retain the original URL spelling and values.
    const decodedKey = decodeURIComponent(key.replace(/\+/g, ' '));
    return isCredentialKey(decodedKey) || /^(?:assertion|code|response)$/i.test(decodedKey)
      ? `${separator}${key}=${REDACTED}` : match;
  } catch (error) {
    // An undecodable name cannot safely be classified as non-sensitive.
    return `${separator}${key}=${REDACTED}`;
  }
};

const bearerPattern = /\b(Bearer|Basic)\s+[^\s"'<>;,}\\]+/gi;
const jwtPattern = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g;
const queryPattern = /([?&;])([^=?&#;\s]+)=([^&#;\s]*)/g;
const userInfoPattern = /(https?:\/\/)[^/\s?#]*@/gi;
const textTokenPattern = new RegExp([bearerPattern, jwtPattern, queryPattern, userInfoPattern]
  .map(pattern => pattern.source).join('|'), 'iy');
const headerNamePattern = /[!#$%&'*+.^_`|~a-z0-9-]+/iy;
const headerSeparatorPattern = /[ \t]*:[ \t]*/y;
const redactTextToken = match => {
  if (match[1]) return `${match[1]} ${REDACTED}`;
  if (match[2]) return redactQuery(match[0], match[2], match[3]);
  if (match[5]) return `${match[5]}${REDACTED}@`;
  return match[0].startsWith('eyJ') ? REDACTED : match[0];
};

// Find a complete JSON container/string without mistaking escaped quotes or
// braces inside strings for boundaries. JSON.parse still validates the syntax.
const jsonEnd = (text, start, depth) => {
  const stack = [];
  let quoted = false;
  for (let index = start; index < text.length; index += 1) {
    const character = text[index];
    if (quoted) {
      if (character === '\\') index += 1;
      else if (character === '"') {
        quoted = false;
        if (!stack.length) return index + 1;
      }
    } else if (character === '"') quoted = true;
    else if (character === '{' || character === '[') {
      stack.push(character === '{' ? '}' : ']');
      if (stack.length + depth > MAX_DEPTH) return -1;
    } else if (character === '}' || character === ']') {
      if (stack.pop() !== character) return -1;
      if (!stack.length) return index + 1;
    }
  }
  return -1;
};

// Return plain, non-mutating diagnostic data. In particular, never carry an
// Axios/native request's toJSON method into a later serializer.
const redactCredentials = input => {
  const seen = new WeakSet();
  const walk = (value, depth, headerPairs = false, onMalformed = () => {}) => {
    if (depth > MAX_DEPTH) return '[Truncated]';
    if (typeof value === 'string') {
      if (value.length > MAX_TEXT_LENGTH) return REDACTED;
      const source = value;
      let text = '';
      let copied = 0;
      let nameEnd = 0;
      for (let index = 0; index < source.length; index += 1) {
        const character = source[index];
        // Plaintext credentials own their whole token. Recognize them before
        // a quote/bracket in the credential can split it into JSON fragments.
        textTokenPattern.lastIndex = index;
        const token = textTokenPattern.exec(source);
        const safeToken = token && redactTextToken(token);
        if (token && safeToken !== token[0]) {
          text += source.slice(copied, index) + safeToken;
          index += token[0].length - 1;
          copied = index + 1;
        } else if (character === '"'
          || (character === '{' && /^\s*(?:["\\}]|$)/.test(source.slice(index + 1)))
          || (character === '[' && /^\s*(?:["{[\]\\]|(?:-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|true|false|null)(?=\s*(?:,|\]|$))|$)/.test(source.slice(index + 1)))) {
          // Keep ordinary placeholders, diagnostic labels and our own markers.
          const end = jsonEnd(source, index, depth);
          if (end === -1) {
            // An unmatched prose quote alone is not structured JSON.
            if (character !== '"' || /^\s*[[{"\\]/.test(source.slice(index + 1))) {
              onMalformed();
              text += source.slice(copied, index) + REDACTED;
              copied = source.length;
              break;
            }
          } else {
            const fragment = source.slice(index, end);
            try {
              const parsed = JSON.parse(fragment);
              // A standalone credential property is incomplete JSON too. Censor
              // its remainder rather than guessing where an arbitrary value ends.
              if (typeof parsed === 'string' && isCredentialKey(parsed) && /^\s*:/.test(source.slice(end))) {
                onMalformed();
                text += source.slice(copied, index) + REDACTED;
                copied = source.length;
                break;
              }
              let malformed = false;
              const safe = walk(parsed, depth + 1, headerPairs, () => { malformed = true; });
              // A quote may actually be a JSON key boundary after a stray prose
              // quote. If its decoded content is malformed JSON, the original
              // closing boundary is ambiguous: censor the entire remainder.
              if (typeof parsed === 'string' && malformed) {
                onMalformed();
                text += source.slice(copied, index) + REDACTED;
                copied = source.length;
                break;
              }
              text += source.slice(copied, index)
                + (typeof parsed === 'string' && safe === parsed ? fragment : JSON.stringify(safe));
            } catch (error) {
              onMalformed();
              text += source.slice(copied, index) + REDACTED;
              // An invalid quoted fragment can hide the next credential-key
              // boundary, including when the fragment contains a raw newline.
              if (character === '"') {
                copied = source.length;
                break;
              }
            }
            copied = end;
            index = end - 1;
          }
        } else if (index >= nameEnd) {
          // Inspect each maximal name once, but keep scanning its characters:
          // names can contain URL separators or embedded plaintext tokens.
          headerNamePattern.lastIndex = index;
          const name = headerNamePattern.exec(source);
          if (name) {
            nameEnd = headerNamePattern.lastIndex;
            headerSeparatorPattern.lastIndex = nameEnd;
            const separator = headerSeparatorPattern.exec(source);
            if (separator && isCredentialKey(name[0])) {
              let end = headerSeparatorPattern.lastIndex;
              while (end < source.length && source[end] !== '\r' && source[end] !== '\n') end += 1;
              text += source.slice(copied, index) + name[0] + separator[0] + REDACTED;
              copied = end;
              index = end - 1;
            }
          }
        }
      }
      return text + source.slice(copied);
    }
    if (typeof value === 'bigint') return String(value);
    if (typeof value === 'function' || typeof value === 'symbol') return undefined;
    if (!value || typeof value !== 'object') return value;
    if (seen.has(value)) return '[Circular]';
    seen.add(value);
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? '[Invalid Date]' : value.toISOString();
    if (typeof Buffer !== 'undefined' && Buffer.isBuffer(value)) {
      return { type: 'Buffer', length: value.length };
    }
    if (typeof ArrayBuffer !== 'undefined' && (value instanceof ArrayBuffer || ArrayBuffer.isView(value))) {
      return { type: 'Binary', byteLength: value.byteLength };
    }
    if (headerPairs && Array.isArray(value)) {
      return value.map((entry, index) => (index % 2 === 1
        && typeof value[index - 1] === 'string' && isCredentialKey(value[index - 1])
        ? REDACTED : walk(entry, depth + 1)));
    }
    const safe = Array.isArray(value) ? [] : {};
    const keys = Object.keys(value);
    if (value instanceof Error) {
      ['name', 'message', 'stack', 'cause'].forEach(key => {
        if (!keys.includes(key)) keys.push(key);
      });
    }
    keys.forEach(key => {
      try {
        if (!['toJSON', '__proto__', 'hasOwnProperty', 'constructor', 'prototype'].includes(key)) {
          Object.defineProperty(safe, key, {
            value: isCredentialKey(key) ? REDACTED
              : walk(value[key], depth + 1, /^(?:rawheaders|rawtrailers|headers)$/i.test(key)),
            enumerable: true,
            writable: true,
            configurable: true,
          });
        }
      } catch (error) {
        safe[key] = '[Unavailable]';
      }
    });
    return safe;
  };
  try { return walk(input, 0); } catch (error) { return '[Unavailable]'; }
};

module.exports = { REDACTED, isCredentialKey, redactCredentials };
